import { makeAutoObservable } from 'mobx';
import React from 'react';

import { GridDataEntry } from 'controls/grid/gridDataEntry';
import { GridPlugin, GridSelectionType } from 'controls/grid/gridConfiguration';
import { GridStore } from 'controls/grid/gridStore';
import { MobxManager } from 'framework/mobx-integration';
import { GridMenuAction } from 'controls/grid/plugins/grid-menu/gridMenuAction';
import { MenuActionsColumn } from 'controls/grid/plugins/grid-menu/menuActionsColumn';
import { AntMenuInfo } from 'controls/react/ant/antMenu';
import { GridSelection } from 'controls/grid/gridSelection';
import { GridColumnConfig } from 'controls/grid/gridColumnConfig';
import { runAsAsync, ZIndex } from 'tools/utils';
import { performAction } from '../../../react/ant/antMessage';
import { InlineActionsColumn } from 'controls/grid/plugins/grid-menu/inlineActionsColumn';
import { IconType, openConfirmMessage } from 'controls/react/ant/antConfirmModal';
import { ModalPosition } from 'controls/react/ant/antModal';
import translator from 'core/localization/localization';

export type GridMenuPluginConfig<DataItem extends GridDataEntry> = {
	actions: GridMenuAction<DataItem>[];
	mainMenu?: boolean | string;
	menuActionsColumn?: boolean | string;
	inlineActionsColumn?: boolean | string;
	onActionFinished?: () => void;
	inlineActionsColumnVisible?: boolean;
};

const i18n = translator({
	'Action icons': {
		no: 'Handlingsikoner',
	},
});

export class GridMenuPlugin<DataItem extends GridDataEntry> implements GridPlugin<DataItem> {
	store: GridStore<DataItem>;

	initialized: boolean = false;

	mobx = new MobxManager();
	id = 'grid-menu';

	constructor(public config: GridMenuPluginConfig<DataItem>) {
		makeAutoObservable(this);
	}

	attach(store: GridStore<DataItem>): Promise<void> {
		this.store = store;
		return Promise.resolve();
	}

	columnsFilter = (columns: GridColumnConfig<DataItem>[]) => {
		if (this.config.menuActionsColumn) {
			const actionsColumn: GridColumnConfig<DataItem> = {
				title: i18n('Actions'),
				field: 'actions',
				sortable: false,
				fixed: 'left',
				expandOnClick: false,
				rendererHeader: () => null,
				renderer: (item, extra) => {
					return {
						content: <MenuActionsColumn item={extra.gridItem} store={this.store} />,
						centerContent: true,
					};
				},
				width: 30,
			};
			columns = [...columns];
			columns.splice(this.store.config.selection != GridSelectionType.None ? 1 : 0, 0, actionsColumn);
		}

		if (this.config.inlineActionsColumn) {
			const actionsColumn: GridColumnConfig<DataItem> = {
				title: i18n('Action icons'),
				field: 'actionIcons',
				sortable: false,
				fixed: 'right',
				expandOnClick: false,
				visible: this.config.inlineActionsColumnVisible != false,
				rendererHeader: () => null,
				renderer: (item, extra) => {
					return {
						content: <InlineActionsColumn item={extra.gridItem} store={this.store} />,
						centerContent: true,
					};
				},
				width: this.config.actions.length * 20,
			};
			columns = [...columns];
			columns.push(actionsColumn);
		}

		return columns;
	};

	getActionsForSelection(selection: GridSelection<DataItem>, actionsConfigRaw?: boolean | string) {
		let actionsConfig = '';
		if (typeof actionsConfigRaw == 'string') {
			actionsConfig = actionsConfigRaw;
		}

		let actions: (GridMenuAction<DataItem> | { type: 'divider' })[];
		let lastItemWasSplitter = false;
		if (actionsConfig?.length > 0) {
			actions = actionsConfig
				.split(' ')
				.map((x) => {
					if (x == '|') {
						if (lastItemWasSplitter) {
							return null;
						}

						lastItemWasSplitter = true;
						return {
							type: 'divider' as const,
						};
					}

					lastItemWasSplitter = false;

					return this.config.actions.find((y) => y.id == x);
				})
				.filter((x) => x != null);
		} else {
			actions = this.config.actions;
		}

		return actions.filter((x) => 'type' in x || (x.visible?.(selection, this.store) ?? true));
	}

	generateContextMenu(selection: GridSelection<DataItem>, actionsConfig?: string) {
		const actions = this.getActionsForSelection(selection, actionsConfig);

		return actions.map((action) => {
			if ('type' in action) return action;

			let disabled = false;
			let title = null;

			if (action.enabled) {
				const result = action.enabled(selection, this.store);
				if (typeof result == 'boolean') {
					disabled = !result;
				} else {
					disabled = !result.enabled;
					title = result.reason;
				}
			}

			return {
				id: action.id,
				icon: action.icon,
				key: action.id,
				label: action.label,
				disabled: disabled,
				title: title,
			};
		});
	}

	getMenuActionByMenuItem(item: AntMenuInfo<DataItem>) {
		return this.config.actions.find((x) => x.id == item.key);
	}

	onMenuItemClick = async (item: AntMenuInfo<DataItem>, selection: GridSelection<DataItem>) => {
		ZIndex.updateZIndex(item.domEvent.target);

		const action = this.getMenuActionByMenuItem(item);
		await this.invokeAction(action, selection);
	};

	invokeAction = async (menuAction: GridMenuAction<DataItem>, selection: GridSelection<DataItem>) => {
		if (menuAction.confirmation?.singularQuestion) {
			const result = await openConfirmMessage({
				title: i18n('Info'),
				okText: i18n('Delete'),
				icon: IconType.Question,
				positionType: ModalPosition.Mouse,
				message:
					selection.selectedItemsCount > 1 && menuAction.confirmation.pluralQuestion
						? menuAction.confirmation.pluralQuestion.replace('{0}', selection.selectedItemsCount.toLocaleString())
						: menuAction.confirmation.singularQuestion,
			});

			if (!result) return;
		}

		const wrapper = <T,>(action: () => Promise<T>) =>
			menuAction.disableSuccessNotification ? action() : performAction(action, undefined, true);

		await runAsAsync(menuAction.action, [selection, this.store], wrapper);
		this.config.onActionFinished?.();
	};
}
